import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { EulaMarkdown } from "../components/legal/EulaMarkdown";
import { EULA_RU_MARKDOWN } from "../legal/eulaRu";
import { useLocale } from "../i18n/LocaleProvider";

import styles from "./EulaModal.module.css";

type EulaModalContextValue = {
  openModal: () => void;
};

const EulaModalContext = createContext<EulaModalContextValue | null>(null);

export function useEulaModal(): EulaModalContextValue {
  const ctx = useContext(EulaModalContext);
  if (!ctx) {
    throw new Error("useEulaModal must be used within EulaModalProvider");
  }
  return ctx;
}

export function EulaModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const { copy, locale } = useLocale();

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeModal();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, closeModal]);

  return (
    <EulaModalContext.Provider value={{ openModal }}>
      {children}
      {open ? (
        <div
          className={styles.overlay}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
          role="presentation"
        >
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="eula-modal-title"
          >
            <h2 id="eula-modal-title" className={styles.title}>
              {copy.eulaModal.title}
            </h2>
            {locale === "en" ? (
              <p className={styles.localeNote}>{copy.eulaModal.localeNote}</p>
            ) : null}
            <div className={styles.content}>
              <EulaMarkdown markdown={EULA_RU_MARKDOWN} />
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={closeModal}
            >
              {copy.eulaModal.close}
            </button>
          </div>
        </div>
      ) : null}
    </EulaModalContext.Provider>
  );
}
